import { useMemo, useRef, useState } from "react";
import {
    AppId,
    CountriesSelect,
    cssX, Dropdown,
    FCX,
    getRem,
    Header,
    HeaderLogo, IconDropdownArrowUp, IDropdownItem,
    Navigation,
    ProductLogo,
} from "@datassential/platform-ui-lib";
import './PlatformHeader.scss';
import cn from "classnames";
import Search from "./Search";
import { matchPath, NavLink, useLocation } from "react-router-dom";
import { Routes } from "@core/routes";
import useOutsideClick from "@hooks/useOutsideClick";

const SubNavItem: FCX<{
    path: string;
}> = ({
    path,
    children,
}) => {
    return (
        <NavLink
            to={path}
            className={({ isActive }) => cn(
                cssX('Dropdown__item'),
                'is-tall',
                'PlatformHeader__nav-sub-item',
                isActive && 'active',
            )}
        >
            {children}
        </NavLink>
    )
}

const PlatformHeader: FCX = () => {
    const [isAnalyzeOpened, setIsAnalyzeOpened] = useState(false);

    const analyzeRef = useRef(null);
    useOutsideClick({
        ref: analyzeRef,
        callback: () => setIsAnalyzeOpened(false),
    });
    const location = useLocation();

    const analyzeLinks = useMemo(
        () => [
            {
                id: 'Trend',
                title: 'Trend',
                path: Routes.Trend,
            },
            {
                id: 'correlation',
                title: 'Correlation',
                path: Routes.Correlation
            },
            {
                id: 'visualize',
                title: 'Visualize',
                path: Routes.Visualize,
            },
        ],
        []
    );

    const analyzeItems = useMemo(
        (): IDropdownItem[] => analyzeLinks
            .map(item => ({
                id: item.id,
                content: (
                    <SubNavItem path={item.path}>
                        {item.title}
                    </SubNavItem>
                ),
            })),
        [analyzeLinks]
    );

    const isAnalyseActive = useMemo(
        () => analyzeLinks.some(item => matchPath(item.path, location.pathname)),
        [analyzeLinks, location.pathname],
    );

    const navigationItems = useMemo(
        () => [
            ...(analyzeLinks.length > 0 ? [{
                id: 'Analyze',
                content: (
                    <div
                        className={cn("PlatformHeader__nav-item", isAnalyseActive && 'active-accessible')}
                        onClick={() => setIsAnalyzeOpened(!isAnalyzeOpened)}
                        onMouseEnter={() => setIsAnalyzeOpened(true)}
                        data-tooltip-id="platform-header-analyze"
                        ref={analyzeRef}
                    >
                        Analyze
                        <IconDropdownArrowUp
                            className="PlatformHeader__nav-icon"
                            style={{ transform: `rotate(${isAnalyzeOpened ? 0 : 180}deg)` }}
                        />
                        <Dropdown
                            isOpened={isAnalyzeOpened}
                            id="platform-header-analyze"
                            place="bottom-end"
                            offset={0}
                            columns={[
                                {
                                    id: 'analyze',
                                    items: analyzeItems,
                                }
                            ]}
                        />
                    </div>
                ),
            }] : []),
            {
                id: 'search',
                content: (
                    <Search/>
                ),
            }
        ],
        [analyzeLinks, analyzeItems, isAnalyzeOpened, isAnalyseActive]
    );

    return (
        <Header
            className="PlatformHeader"
            leftContent={<>
                <a href={Routes.Home}>
                    <HeaderLogo>
                        <ProductLogo appId={AppId.ConsumerPreferences}/>
                    </HeaderLogo>
                </a>
                <CountriesSelect
                    options={[
                        { id: 'usa', shortName: 'USA', name: '' },
                    ]}
                    value={'usa'}
                    setValue={() => {
                    }}
                    isDisabled
                    style={{ marginLeft: getRem(10) }}
                    testId={"selectDEMOHeaderCountry"}
                />
            </>}
            rightContent={<Navigation data={navigationItems}/>}
        />
    );
}

export default PlatformHeader;